import React from "react";
import { AbsoluteFill, interpolate, spring, useCurrentFrame, useVideoConfig } from "remotion";
import { COLORS, FONTS, SPRING, STAKE } from "./constants";
import { Backdrop, SceneTitle, glow } from "./shared";

export const Scene09CSSolve: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps, durationInFrames } = useVideoConfig();

  const fade = interpolate(frame, [durationInFrames - 30, durationInFrames], [1, 0], {
    extrapolateLeft: "clamp", extrapolateRight: "clamp",
  });

  const titleAp = interpolate(spring({ frame, fps, config: SPRING.snappy }), [0, 1], [0, 1], {
    extrapolateLeft: "clamp", extrapolateRight: "clamp",
  });

  // Type out query
  const query = "what did we promise Acme about data residency?";
  const chars = Math.floor(interpolate(frame, [30, 120], [0, query.length], {
    extrapolateLeft: "clamp", extrapolateRight: "clamp",
  }));

  const answerSp = spring({ frame: frame - 140, fps, config: SPRING.gentle });
  const ansOp = interpolate(answerSp, [0, 1], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });

  return (
    <AbsoluteFill style={{ background: COLORS.bg, opacity: fade, overflow: "hidden" }}>
      <Backdrop frame={frame} accent={STAKE.success} />

      <SceneTitle
        text="EAKC remembers."
        subtitle="Every interaction. Every promise. Every clause. One query away."
        color={STAKE.success}
        frame={frame}
        appear={titleAp}
      />

      {/* Query prompt bar */}
      <div style={{
        position: "absolute",
        top: 280,
        left: "50%",
        transform: `translateX(-50%) perspective(1200px) rotateX(3deg)`,
        width: 1500,
      }}>
        <div style={{
          background: COLORS.bgCardLight,
          border: `2px solid ${STAKE.success}`,
          borderRadius: 16,
          padding: "24px 32px",
          fontSize: 32,
          fontFamily: FONTS.mono,
          color: COLORS.textPrimary,
          boxShadow: `0 0 40px ${STAKE.success}44`,
          display: "flex",
          alignItems: "center",
          gap: 16,
        }}>
          <span style={{ color: STAKE.success, fontWeight: 900 }}>{">"}</span>
          <span>{query.slice(0, chars)}</span>
          {chars < query.length && (
            <span style={{
              display: "inline-block",
              width: 3,
              height: 32,
              background: STAKE.success,
              marginLeft: 2,
              opacity: Math.sin(frame * 0.3) > 0 ? 1 : 0,
            }} />
          )}
        </div>
      </div>

      {/* Answer card */}
      <div style={{
        position: "absolute",
        top: 430,
        left: "50%",
        transform: `translateX(-50%) perspective(1200px) rotateX(-2deg)`,
        width: 1500,
        opacity: ansOp,
      }}>
        <div style={{
          background: `linear-gradient(135deg, ${STAKE.success}15, ${COLORS.bgCard}ee)`,
          border: `2.5px solid ${STAKE.success}`,
          borderRadius: 20,
          padding: "36px 44px",
          boxShadow: `0 0 60px ${STAKE.success}55, 0 20px 50px rgba(0,0,0,0.6)`,
        }}>
          <div style={{
            fontSize: 16,
            fontFamily: FONTS.mono,
            color: STAKE.success,
            marginBottom: 16,
            letterSpacing: 2,
            textTransform: "uppercase",
          }}>EAKC · cited answer</div>

          <div style={{
            fontSize: 32,
            fontFamily: FONTS.body,
            color: COLORS.textPrimary,
            lineHeight: 1.5,
            marginBottom: 28,
          }}>
            Acme Corp's MSA §4.2 (signed 2025-11-08) commits to
            {" "}<span style={{ color: STAKE.success, fontWeight: 800, textShadow: glow(STAKE.success) }}>
              data residency in India-region (ap-south-1) only
            </span>
            {" "}with zero cross-region replication. This was reaffirmed in the
            {" "}<span style={{ color: COLORS.neonCyan }}>2026-02-14 security review call</span>{" "}
            when their CISO flagged compliance concerns.
          </div>

          {/* Source chips */}
          <div style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
            {[
              { src: "contracts/acme-msa-v3.pdf §4.2", c: COLORS.neonYellow },
              { src: "email:ciso@acme 2026-02-14", c: COLORS.neonBlue },
              { src: "salesforce:acme-account-notes", c: COLORS.neonPink },
              { src: "slack:#acme-account 2026-02-15", c: COLORS.neonPurple },
            ].map((s, i) => {
              const sp = spring({ frame: frame - 180 - i * 8, fps, config: SPRING.bouncy });
              const op = interpolate(sp, [0, 1], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
              const sc = interpolate(sp, [0, 1], [0.5, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
              return (
                <div key={i} style={{
                  opacity: op,
                  transform: `scale(${sc})`,
                  fontSize: 17,
                  fontFamily: FONTS.mono,
                  color: s.c,
                  padding: "10px 20px",
                  borderRadius: 12,
                  background: `${s.c}12`,
                  border: `1.5px solid ${s.c}77`,
                  textShadow: glow(s.c),
                }}>
                  [{i + 1}] {s.src}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Bottom emphasis */}
      <div style={{
        position: "absolute", bottom: 60, left: 0, right: 0,
        textAlign: "center",
        opacity: interpolate(spring({ frame: frame - 260, fps, config: SPRING.gentle }), [0, 1], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" }),
      }}>
        <div style={{
          fontSize: 38,
          fontFamily: FONTS.heading,
          fontWeight: 800,
          color: COLORS.textPrimary,
          textShadow: `0 0 30px ${STAKE.success}aa`,
        }}>
          Not hallucinated.
          {" "}<span style={{ color: STAKE.success, textShadow: glow(STAKE.success, true) }}>Sourced.</span>
        </div>
      </div>
    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
